import React, { useCallback, useContext, useMemo, useState } from "react";

import SelectActionItemGroup from "~/actionItems/actionItemGroup/SelectActionItemGroup";
import { StatusCondition } from "~/actionItems/enum";
import NewActionItem from "~/actionItems/NewActionItem";
import { AppContext } from "~/common";
import { Button } from "~/ui";
import type { UserAttrs } from "~/ui/legacy/Avatar";
import { Modal, Switch } from "~/ui/legacyFellowIndex";

import styles from "./NewActionItemModal.module.css";

interface NewActionItemModalProps {
	onClose(): void;
	createItem(
		content: string,
		groupId?: string,
		dueDate?: string,
		assignees?: UserAttrs[],
		statusCondition?: StatusCondition,
	): void;
	groupId?: string;
	dueDate?: string;
	assignees?: Array<UserAttrs>;
	hasAccessToDueDates: boolean;
	hasAccessToMultipleAssignees: boolean;
	allowCompleteByAny: boolean;
}

export default function NewActionItemModal({
	onClose,
	createItem,
	groupId: initialGroupId,
	dueDate: initialDueDate,
	assignees: initialAssignees,
	hasAccessToDueDates,
	hasAccessToMultipleAssignees,
	allowCompleteByAny,
}: NewActionItemModalProps) {
	const { user } = useContext(AppContext);
	const [groupId, setGroupId] = useState<string | undefined>(initialGroupId);
	const [content, setContent] = useState<string>("");
	const [dueDate, setDueDate] = useState<string | undefined>(initialDueDate);
	const [assignees, setAssignees] = useState<UserAttrs[] | undefined>(initialAssignees || []);
	const [statusCondition, setStatusCondition] = useState<StatusCondition>(StatusCondition.SINGLE);
	const [createMore, setCreateMore] = useState<boolean>(false);
	const [textVersion, setTextVersion] = useState<number>(0);

	const resetContent = useCallback(() => {
		setTextVersion(textVersion => textVersion + 1);
	}, []);

	const onGroupIdChanged = useCallback((newGroupId: string) => {
		setGroupId(newGroupId);
	}, []);

	const onContentChanged = useCallback((newContent: string) => {
		setContent(newContent);
	}, []);

	const onDueDateChanged = useCallback((newDueDate: string) => {
		setDueDate(newDueDate);
	}, []);

	const onAssigneesChanged = useCallback((newAssignees?: UserAttrs[]) => {
		setAssignees(newAssignees || []);
	}, []);

	const onStatusConditionChanged = useCallback((newStatusCondition: StatusCondition) => {
		setStatusCondition(newStatusCondition);
	}, []);

	const onCreateMoreToggle = useCallback(() => setCreateMore(createMore => !createMore), []);

	const onCancel = useCallback(() => {
		onClose();
	}, [onClose]);

	const onCreate = useCallback(
		(submittedContent?: string) => {
			createItem(submittedContent || content, groupId, dueDate, assignees, statusCondition);
			if (!createMore) {
				onClose();
			}
			resetContent();
		},
		[createItem, groupId, content, dueDate, assignees, statusCondition, createMore, resetContent, onClose],
	);

	const onClickCreate = useCallback(() => {
		onCreate();
	}, [onCreate]);

	const isUserAnAssignee = useMemo(() => {
		return Boolean(assignees?.find(assignee => assignee.id === user?.id));
	}, [assignees, user]);

	const onKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === "Escape") {
				onClose();
			}
		},
		[onClose],
	);

	return (
		<Modal onClose={onClose}>
			<Modal.Header onClose={onClose} noBottomBorder className={styles.header}>
				{isUserAnAssignee && <SelectActionItemGroup groupId={groupId} onGroupIdChanged={onGroupIdChanged} />}
			</Modal.Header>
			<Modal.Body className={styles.body} onKeyDown={onKeyDown}>
				<NewActionItem
					onContentChanged={onContentChanged}
					dueDate={dueDate}
					assignees={assignees}
					statusCondition={statusCondition}
					onDueDateChanged={onDueDateChanged}
					onAssigneesChanged={onAssigneesChanged}
					onStatusConditionChanged={onStatusConditionChanged}
					hasAccessToDueDates={hasAccessToDueDates}
					hasAccessToMultipleAssignees={hasAccessToMultipleAssignees}
					allowCompleteByAny={allowCompleteByAny}
					onSubmit={onCreate}
					textVersion={textVersion}
				/>
			</Modal.Body>
			<Modal.Footer noTopBorder>
				<div className={styles.createMore} onClick={onCreateMoreToggle}>
					<Switch size="small" checked={createMore} />
					Create more
				</div>
				<Button onClick={onCancel} size="normal">
					Cancel
				</Button>
				<Button variant="primary" disabled={!content} onClick={onClickCreate} size="normal">
					Create
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
